// src/features/system/archiver/components/ArchiveStatusView.tsx
// ────────────────────────────────────────────────────────────────────────────
// Archive Pipeline — Domain Presenter (SINGLE SOURCE OF TRUTH).
//
// Pure presentational component — zero hooks, zero fetch, zero side effects.
// Renders archive pipeline status from a typed props interface.
//
// Consumers:
//   - ArchiverDashboard.tsx (SPA admin tab) → feeds data from useArchiver()
//   - ArchiverCard.tsx (agent-chat card) → feeds data from message.metadata
//
// PAC Glass design system: transparent surfaces, divide-based structure,
// no opaque backgrounds.
// ────────────────────────────────────────────────────────────────────────────
import React, { memo, useMemo } from 'react';
import { CollapsibleSection, KpiCell, MiniBar, StatusIndicator } from '@/shared/components/dashboard';
import { humanBytes, fmtNum, fmtDate, fmtTimestamp } from '@/shared/components/dashboard';

// ── Types ────────────────────────────────────────────────────────────────

export interface ArchiveDbInfo {
  total_partitions: number;
  total_rows: number;
  total_bytes?: number;
  total_size: string;
}

export interface ArchiveTotalsInfo {
  total_archived_rows: number;
  distinct_ns: number;
  total_parquet_files: number;
  total_parquet_bytes: number;
}

export interface SavingsInfo {
  compression_ratio: number;
  saved_bytes: number;
  saved_size?: string;
}

export interface PipelineInfo {
  retention_days: number;
  cron_schedule?: string;
}

export interface NamespaceBreakdownItem {
  namespace: string;
  rows: number;
  bytes?: number;
  files?: number;
}

export interface PartitionItem {
  name: string;
  date?: string;
  rows?: number;
  size?: string;
}

export interface SummaryRow {
  scope_ns: string;
  period_start: string;
  period_end: string;
  total_rows: number;
  parquet_files: number;
  parquet_bytes?: number;
  created_at: string;
}

export interface ArchiveStatusViewProps {
  db?: ArchiveDbInfo | null;
  archive?: ArchiveTotalsInfo | null;
  savings?: SavingsInfo | null;
  pipeline?: PipelineInfo | null;
  namespaceBreakdown?: NamespaceBreakdownItem[];
  partitions?: PartitionItem[];
  summaryRows?: SummaryRow[];
  /** Compact mode for agent-chat card context */
  compact?: boolean;
}

// ── Color palette for namespace bars ─────────────────────────────────────

const NS_COLORS = [
  '#34d399', '#60a5fa', '#f97316', '#a78bfa', '#f43f5e',
  '#fbbf24', '#06b6d4', '#84cc16', '#e879f9', '#fb923c',
];

const NS_TW_COLORS = [
  'bg-emerald-500/50', 'bg-sky-500/50', 'bg-orange-500/50', 'bg-violet-500/50', 'bg-rose-500/50',
  'bg-amber-500/50', 'bg-cyan-500/50', 'bg-lime-500/50', 'bg-fuchsia-500/50', 'bg-orange-400/50',
];

// ── Presenter ────────────────────────────────────────────────────────────

function ArchiveStatusView({
  db, archive, savings, pipeline,
  namespaceBreakdown = [],
  partitions = [],
  summaryRows = [],
  compact = false,
}: ArchiveStatusViewProps) {
  const dbRows = db?.total_rows ?? 0;
  const archivedRows = archive?.total_archived_rows ?? 0;
  const pipelineActive = !!(pipeline?.cron_schedule || pipeline?.retention_days);

  const maxNsRows = useMemo(() => {
    if (!namespaceBreakdown.length) return 1;
    return Math.max(...namespaceBreakdown.map(n => n.rows), 1);
  }, [namespaceBreakdown]);

  return (
    <div className="bg-transparent rounded-lg overflow-hidden border border-white/[0.08]">
      {/* ── Header ──────────────────────────────────────────────────── */}
      <div className="bg-transparent flex items-center justify-between gap-2 px-3 py-1.5 border-b border-white/[0.04]">
        <div className="flex items-center gap-2">
          <span className="px-1.5 py-0.5 rounded bg-emerald-600/20 text-emerald-400 text-[10px] font-mono uppercase tracking-wide">
            archive pipeline
          </span>
          {pipelineActive && (
            <StatusIndicator
              active
              label={`${pipeline?.retention_days ?? 7}d retention`}
            />
          )}
        </div>
        {pipeline?.cron_schedule && (
          <span className="text-[9px] text-neutral-600 font-mono">{pipeline.cron_schedule}</span>
        )}
      </div>

      {/* ── KPI Row 1: Database state ───────────────────────────────── */}
      <div className="grid grid-cols-3 divide-x divide-white/[0.04] border-b border-white/[0.04]">
        <KpiCell
          label="Partitions"
          value={fmtNum(db?.total_partitions ?? 0)}
          sub={db?.total_size ?? '—'}
          size={compact ? 'sm' : 'md'}
        />
        <KpiCell
          label="Live Rows"
          value={fmtNum(dbRows)}
          sub="still in PG"
          size={compact ? 'sm' : 'md'}
        />
        <KpiCell
          label="Archived"
          value={fmtNum(archivedRows)}
          sub={`${archive?.distinct_ns ?? 0} namespaces`}
          accent="text-emerald-400"
          size={compact ? 'sm' : 'md'}
        />
      </div>

      {/* ── KPI Row 2: Archive output ───────────────────────────────── */}
      <div className="grid grid-cols-3 divide-x divide-white/[0.04] border-b border-white/[0.04]">
        <KpiCell
          label="Parquet Files"
          value={fmtNum(archive?.total_parquet_files ?? 0)}
          sub={humanBytes(archive?.total_parquet_bytes ?? 0)}
          accent="text-violet-400"
          size={compact ? 'sm' : 'md'}
        />
        <KpiCell
          label="Compression"
          value={savings && savings.compression_ratio > 0
            ? `${savings.compression_ratio.toFixed(0)}:1` : '—'}
          sub={savings && savings.saved_bytes > 0
            ? `${humanBytes(savings.saved_bytes)} saved` : '—'}
          accent="text-orange-400"
          size={compact ? 'sm' : 'md'}
        />
        <KpiCell
          label="Retention"
          value={`${pipeline?.retention_days ?? 7}d`}
          sub="before drop"
          size={compact ? 'sm' : 'md'}
        />
      </div>

      {/* ── Archive progress note ───────────────────────────────────── */}
      {dbRows > 0 && archivedRows > 0 && (
        <div className="px-3 py-1.5 border-b border-white/[0.04] text-[10px] text-neutral-500">
          <span className="text-amber-400/80">⚠</span>{' '}
          {fmtNum(dbRows)} rows remain in PG — archiver exports to Parquet but partitions
          are only dropped after the {pipeline?.retention_days ?? 7}d retention window.
          {archivedRows < dbRows && (
            <> Only {((archivedRows / dbRows) * 100).toFixed(1)}% archived so far.</>
          )}
        </div>
      )}

      {/* ── Archive History Timeline ────────────────────────────────── */}
      {summaryRows.length > 0 && (
        <CollapsibleSection title="Archive History" count={summaryRows.length}>
          <div className="space-y-0.5">
            <div className="flex items-center text-[9px] text-neutral-600 uppercase tracking-wider gap-1 mb-1">
              <span className="w-20">When</span>
              <span className="flex-1">Namespace</span>
              <span className="w-14 text-right">Period</span>
              <span className="w-12 text-right">Rows</span>
              <span className="w-10 text-right">Files</span>
            </div>
            {summaryRows.slice(0, compact ? 20 : 50).map((r, i) => (
              <div key={i} className="flex items-center text-[10px] gap-1 py-0.5">
                <span className="w-20 text-neutral-600 font-mono truncate">
                  {fmtTimestamp(r.created_at)}
                </span>
                <span className="flex-1 text-neutral-400 font-mono truncate">{r.scope_ns}</span>
                <span className="w-14 text-right text-neutral-600 font-mono">
                  {fmtDate(r.period_start)}
                </span>
                <span className="w-12 text-right text-neutral-300 tabular-nums">{fmtNum(r.total_rows)}</span>
                <span className="w-10 text-right text-neutral-500 tabular-nums">{fmtNum(r.parquet_files)}</span>
              </div>
            ))}
            {summaryRows.length > (compact ? 20 : 50) && (
              <p className="text-[9px] text-neutral-600 pt-1">
                +{summaryRows.length - (compact ? 20 : 50)} older entries
              </p>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* ── Namespace Breakdown ─────────────────────────────────────── */}
      {namespaceBreakdown.length > 0 && (
        <CollapsibleSection title="Archived by Namespace" count={namespaceBreakdown.length}>
          {compact ? (
            /* Card-compact: inline bars */
            <div className="space-y-1.5">
              {namespaceBreakdown.slice(0, 10).map((ns, i) => (
                <div key={ns.namespace} className="flex items-center gap-1.5 text-[10px]">
                  <span className="text-neutral-400 truncate w-28 font-mono">{ns.namespace}</span>
                  <div className="flex-1 h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
                    <div
                      className={`h-full rounded-full ${NS_TW_COLORS[i % NS_TW_COLORS.length]}`}
                      style={{ width: `${Math.max((ns.rows / maxNsRows) * 100, 2)}%` }}
                    />
                  </div>
                  <span className="w-12 text-right text-neutral-300 tabular-nums">{fmtNum(ns.rows)}</span>
                </div>
              ))}
              {namespaceBreakdown.length > 10 && (
                <p className="text-[9px] text-neutral-600">+{namespaceBreakdown.length - 10} more</p>
              )}
            </div>
          ) : (
            /* Dashboard-full: MiniBar component */
            <MiniBar
              items={namespaceBreakdown.slice(0, 12).map((ns, i) => ({
                label: ns.namespace,
                value: ns.rows,
                color: NS_COLORS[i % NS_COLORS.length],
              }))}
            />
          )}
        </CollapsibleSection>
      )}

      {/* ── Active Partitions ───────────────────────────────────────── */}
      {partitions.length > 0 && (
        <CollapsibleSection title="Active Partitions" count={partitions.length} defaultOpen={false}>
          <div className="flex flex-wrap gap-1">
            {partitions.slice(0, 20).map((p) => (
              <span
                key={p.name}
                className="px-1.5 py-0.5 rounded-full text-[9px] text-neutral-400 font-mono
                           border border-white/[0.06]"
                title={`${p.rows != null ? fmtNum(p.rows) : '?'} rows · ${p.size ?? '?'}`}
              >
                {p.date?.replace(/_/g, '-') ?? p.name}
              </span>
            ))}
            {partitions.length > 20 && (
              <span className="text-[9px] text-neutral-600 self-center">
                +{partitions.length - 20} more
              </span>
            )}
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
}

export default memo(ArchiveStatusView);
